import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import {
  TrendingUp, ShoppingCart, Package, Store, Repeat, Loader2,
  ChevronDown, Trophy, Image as ImageIcon, Globe,
} from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const imgSrc = (im) => (im?.storage_path ? fileUrl(im.storage_path) : im?.url);
const fmt = (dt) => dt.toISOString().slice(0, 10);

function Bars({ items, empty }) {
  if (!items || items.length === 0) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <div className="space-y-1.5">
      {items.map((b) => (
        <div key={b.label} className="flex items-center gap-2">
          <span className="w-14 shrink-0 truncate text-xs text-muted-foreground">{b.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-brema-bg">
            <div className="h-full rounded-full bg-brand" style={{ width: `${b.pct}%` }} />
          </div>
          <span className="w-24 shrink-0 text-right text-xs font-medium text-foreground">{b.pct}% ({b.units})</span>
        </div>
      ))}
    </div>
  );
}

function ProductRow({ p, rank, sort }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-brema-card" data-testid={`stats-product-${rank}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`stats-product-toggle-${rank}`}
        className="flex w-full items-center gap-3 p-3 text-left"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brema-bg text-xs font-bold text-brand">{rank}</span>
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-border bg-brema-bg">
          {imgSrc(p.image)
            ? <img src={imgSrc(p.image)} alt={p.name} className="h-full w-full object-cover" />
            : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-4 w-4" /></div>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">{p.name}</p>
          {p.line && <p className="text-xs text-muted-foreground">{p.line}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="font-semibold text-foreground">{sort === "quantity" ? `${p.units} pz` : eur(p.revenue)}</p>
          <p className="text-xs text-muted-foreground">{sort === "quantity" ? eur(p.revenue) : `${p.units} pz`}</p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-4 border-t border-border p-4 sm:grid-cols-2" data-testid={`stats-product-breakdown-${rank}`}>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Taglie</p>
            <Bars items={p.size_breakdown} empty="Nessun dato taglie" />
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Colori</p>
            <Bars items={p.color_breakdown} empty="Dato colore non disponibile" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SalesStats() {
  const { theme } = useTheme();
  const [from, setFrom] = useState(fmt(new Date(Date.now() - 365 * 864e5)));
  const [to, setTo] = useState(fmt(new Date()));
  const [line, setLine] = useState("tutte");
  const [nation, setNation] = useState("tutte");
  const [sort, setSort] = useState("revenue");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { from_date: from, to_date: to, sort };
    if (line !== "tutte") params.line = line;
    if (nation !== "tutte") params.nation = nation;
    api.get("/reports/sales-stats", { params })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Caricamento statistiche fallito"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [sort]);

  const k = data?.kpis;
  const marketData = (data?.nations || []).map((n) => ({ name: n.nation, value: n.revenue }));

  return (
    <div>
      <PageHeader
        eyebrow="Business Intelligence · Brema Bike"
        title="Statistiche di Vendita"
        subtitle="Analisi vendite B2B del mondo Bike: capi più venduti, taglie/colori, mercati e performance rivenditori."
      />

      {/* Filtri */}
      <div className="mb-6 rounded-xl border border-border bg-brema-card p-5" data-testid="stats-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Dal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="stats-from" className="h-9 w-[150px] bg-brema-bg" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Al</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="stats-to" className="h-9 w-[150px] bg-brema-bg" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Linea / Collezione</Label>
            <Select value={line} onValueChange={setLine}>
              <SelectTrigger data-testid="stats-line" className="h-9 w-[190px] bg-brema-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le collezioni</SelectItem>
                {(data?.available_collections || []).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Nazione / Area</Label>
            <Select value={nation} onValueChange={setNation}>
              <SelectTrigger data-testid="stats-nation" className="h-9 w-[190px] bg-brema-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutti i mercati</SelectItem>
                {(data?.available_nations || []).map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading} data-testid="stats-apply"
            className="h-9 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna"}
          </Button>
        </div>
      </div>

      {!data ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
      ) : (
        <div className="space-y-8">
          {/* KPI */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="stats-kpis">
            <StatCard icon={TrendingUp} label="Fatturato (IVA escl.)" value={eur(k.revenue)} />
            <StatCard icon={Package} label="Pezzi venduti" value={k.units} delay={0.05} />
            <StatCard icon={ShoppingCart} label="Ordini" value={k.orders} delay={0.1} accent="grey" />
            <StatCard icon={Store} label="Rivenditori attivi" value={k.active_resellers} delay={0.15} />
            <StatCard icon={Repeat} label="Tasso riassortimento" value={`${k.reassortment_rate}%`} hint={`${k.reorder_resellers} con 2+ ordini`} delay={0.2} accent="red" />
          </div>

          {/* Capi più venduti */}
          <section>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-foreground">
                <Trophy className="h-5 w-5 text-brand" />
                <h2 className="text-lg font-semibold">Capi più venduti</h2>
              </div>
              <div className="ml-auto flex items-center gap-1 rounded-full border border-border bg-brema-card p-1" data-testid="stats-sort-toggle">
                <button onClick={() => setSort("revenue")} data-testid="stats-sort-revenue"
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${sort === "revenue" ? "bg-selected text-selected-foreground" : "text-muted-foreground hover:text-foreground"}`}>Per fatturato</button>
                <button onClick={() => setSort("quantity")} data-testid="stats-sort-quantity"
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${sort === "quantity" ? "bg-selected text-selected-foreground" : "text-muted-foreground hover:text-foreground"}`}>Per quantità</button>
              </div>
            </div>
            {data.top_products.length === 0 ? (
              <p className="rounded-xl border border-border bg-brema-card p-6 text-center text-sm text-muted-foreground" data-testid="stats-empty">
                Nessuna vendita Bike nel periodo o nella selezione corrente.
              </p>
            ) : (
              <div className="space-y-2" data-testid="stats-top-products">
                {data.top_products.map((p, i) => (<ProductRow key={p.product_id || i} p={p} rank={i + 1} sort={sort} />))}
              </div>
            )}
          </section>

          {/* Vendite per mercato */}
          {marketData.length > 0 && (
            <section>
              <div className="mb-4 flex items-center gap-2 text-foreground">
                <Globe className="h-5 w-5 text-brand" />
                <h2 className="text-lg font-semibold">Vendite per mercato</h2>
              </div>
              <div className="rounded-xl border border-border bg-brema-card p-6" data-testid="stats-market-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={marketData}>
                    <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => eur(v)}
                      contentStyle={{ background: "#1D2433", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {marketData.map((_, i) => (<Cell key={i} fill={theme === "dark" ? "#FACC15" : "#B91C1C"} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          {/* Performance rivenditori */}
          <section>
            <div className="mb-4 flex items-center gap-2 text-foreground">
              <Store className="h-5 w-5 text-brand" />
              <h2 className="text-lg font-semibold">Performance rivenditori</h2>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border bg-brema-card">
              <table className="w-full text-sm" data-testid="stats-resellers-table">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">Rivenditore</th>
                    <th className="p-3 text-right">Ordini</th>
                    <th className="p-3 text-right">Pezzi</th>
                    <th className="p-3 text-right">Fatturato</th>
                    <th className="p-3 text-center">Riassortimento</th>
                  </tr>
                </thead>
                <tbody>
                  {data.resellers.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nessun dato.</td></tr>
                  ) : data.resellers.map((r, i) => (
                    <tr key={r.reseller_id || i} data-testid={`stats-reseller-row-${i}`} className="border-b border-border text-foreground last:border-0">
                      <td className="p-3 text-muted-foreground">{i + 1}</td>
                      <td className="p-3 font-medium">{r.name}</td>
                      <td className="p-3 text-right">{r.orders}</td>
                      <td className="p-3 text-right">{r.units}</td>
                      <td className="p-3 text-right font-semibold">{eur(r.revenue)}</td>
                      <td className="p-3 text-center">
                        {r.reassortment
                          ? <span className="rounded-full bg-positive/15 px-2 py-0.5 text-xs font-medium text-positive">Sì (2+ ordini)</span>
                          : <span className="rounded-full bg-brema-bg px-2 py-0.5 text-xs text-muted-foreground">No</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
